import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, CircleChevronLeft, Sparkles } from "lucide-react";
import toast from "react-hot-toast";
import { useRouter } from "@tanstack/react-router";
import { InverntroyService } from "../../services/api/inventroy.service";
import { ProductCatalogService } from "../../services/api/productCatalog.service";
import { ProductFormSkeleton } from "./AddProductLoading";
import DemoImage from "../../assets/images/default_product.jpg";

import { ProductEnrichPanel } from "./ProductEnrichPanel";
import { BasicDetailsTab } from "./tabs/BasicDetailsTab";
import {
  ProductSaltDtlsTab,
  SaltRow,
} from "./tabs/ProductSaltDtlsTab";
import { ProductPackagingDtlsTab } from "./tabs/ProductPackagingDtlsTab";
import { ProductAttributesTab } from "./tabs/ProductAttributesTab";
import {
  CategoryMappingRow,
  ProductCategoryMappingTab,
} from "./tabs/ProductCategoryMappingTab";
import { ProductGstTab } from "./tabs/ProductGstTab";
import { ProductInfoTab } from "./tabs/ProductInfoTab";
import { ProductCategoryTab } from "./tabs/ProductCategoryTab";

interface AddNewProductModalProps {
  onClose: () => void;
  productData?: any;
  onSuccess: () => void;
  productId: string;
  categoryId: number;
}

type TabId =
  | "basic"
  | "salts"
  | "packaging"
  | "gst"
  | "attributes"
  | "category"
  | "info"
  | "listing";

// Which form fields belong to which step. "images" stands in for the media
// pickers, which are several fields on the form but one call to the service.
const BASIC_FIELDS = [
  "name",
  "description",
  "brandName",
  "marketerId",
  "countryOfOrigin",
  "isMedicine",
  "isRxRequired",
  "isPublished",
  "isActive",
  "isReturnable",
  "discretePackaging",
  "coldChainRequired",
  "clinicOnly",
  "storageInstructions",
  "status",
  "tabletPerStripe",
  "medicineScheduleId",
  "images",
];

const SALT_FIELDS = ["salts", "saltCompositionText"];

const PACKAGING_FIELDS = [
  "packagingText",
  "packType",
  "packUnit",
  "packQuantity",
  "baseUnit",
  "multipackCount",
  "comboItems",
  "offerFreeQty",
];

const GST_FIELDS = ["hsnNumber", "cgst", "sgst"];

const MEDICINE_ATTR_FIELDS = [
  "medProductFormId",
  "releaseType",
  "disintegrationType",
  "route",
];

const PRODUCT_ATTR_FIELDS = [
  "prodProductFormId",
  "variant",
  "flavour",
  "targetGroup",
  "ageMinMonths",
  "ageMaxMonths",
  "dietType",
  "sugarStatus",
  "attrKeyIngredients",
];

const CATEGORY_FIELDS = ["categoryMappings", "domainId", "regulatoryDomainId"];

const INFO_FIELDS = [
  "introduction",
  "howToUse",
  "safetyAdvise",
  "ifMiss",
  "factBox",
  "primaryUse",
  "storage",
  "commonSideEffect",
  "alcoholInteraction",
  "pregnancyInteraction",
  "lactationInteraction",
  "drivingInteraction",
  "kidneyInteraction",
  "liverInteraction",
  "qAndA",
  "howItWorks",
  "interaction",
  "keyIngredients",
  "keyBenefits",
  "source",
  "productHighlights",
];

const LISTING_FIELDS = ["assignedListingCategories"];

const TAB_FIELDS: Record<TabId, string[]> = {
  basic: BASIC_FIELDS,
  salts: SALT_FIELDS,
  packaging: PACKAGING_FIELDS,
  gst: GST_FIELDS,
  attributes: [...MEDICINE_ATTR_FIELDS, ...PRODUCT_ATTR_FIELDS],
  category: CATEGORY_FIELDS,
  info: INFO_FIELDS,
  listing: LISTING_FIELDS,
};

/** Reverse of TAB_FIELDS: the step a given field is edited on. */
const FIELD_TABS: Record<string, TabId> = (() => {
  const map: Record<string, TabId> = {};
  (Object.keys(TAB_FIELDS) as TabId[]).forEach((tab) => {
    TAB_FIELDS[tab].forEach((field) => {
      map[field] = tab;
    });
  });
  return map;
})();

/**
 * Add / update product.
 *
 * The catalogue itself is written through the V3 endpoints, which own
 * product_mstr, product_pkgn_dtls, product_attributes, medicine_attributes,
 * medicine_salt_dtls and product_category_mapping, and record an audit row for
 * every write. GST, long-form product info, listing categories and images are
 * not part of V3, so those four keep using their existing V2 endpoints.
 *
 * In create mode the steps run as a wizard and are submitted together; in edit
 * mode each section saves on its own, because composition and category mappings
 * are set-valued and have replace endpoints of their own.
 */
export function AddNewProductModal({
  productId,
  onSuccess,
  onClose,
  categoryId,
}: AddNewProductModalProps) {
  const router = useRouter();
  const pathname = router.state.location.pathname;
  const isEdit = !!productId;

  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>("basic");

  /**
   * `isMedicine` as it is stored, so flipping it to No in edit mode can clear
   * the composition that is already persisted. The backend leaves those salt
   * rows alone, and a non-medicine product carrying salts reads back wrong.
   */
  const [storedIsMedicine, setStoredIsMedicine] = useState(false);

  const [isEnrichOpen, setIsEnrichOpen] = useState(false);

  /**
   * Fields that have changed since the product was loaded and have not been
   * saved yet, used to mark the steps that still need their Update button.
   *
   * Each save clears the fields it actually persisted rather than the fields of
   * its own step, because the two do not always match: saving Basic Details
   * also writes the domain, and saving the composition also writes the medicine
   * attributes. In create mode nothing is saved step by step, so the marks mean
   * something slightly different there - they record what the AI filled in, so
   * the reviewer knows which steps to read before pressing Create.
   */
  const [pendingFields, setPendingFields] = useState<Record<string, true>>({});

  const markPending = (fields: string[]) =>
    setPendingFields((prev) => {
      const next = { ...prev };
      fields.forEach((field) => {
        next[field] = true;
      });
      return next;
    });

  const clearPending = (fields: string[]) =>
    setPendingFields((prev) => {
      const next = { ...prev };
      fields.forEach((field) => delete next[field]);
      return next;
    });

  const [listingCategories, setListingCategories] = useState<
    { id: number; name: string }[]
  >([]);
  const [marketerOptions, setMarketerOptions] = useState<
    { id: string; marketerName: string }[]
  >([]);
  const [scheduleOptions, setScheduleOptions] = useState<
    { id: number; name: string }[]
  >([]);

  const [formData, setFormData] = useState({
    // ---------------------------------------------------- product_mstr
    name: "",
    description: "",
    brandName: "",
    marketerId: "",
    countryOfOrigin: "",
    categoryId: categoryId || 1,
    isMedicine: false,
    isRxRequired: null as boolean | null,
    isPublished: null as boolean | null,
    isActive: null as boolean | null,
    isReturnable: true,
    discretePackaging: false,
    coldChainRequired: false,
    clinicOnly: false,
    storageInstructions: "",
    status: "",
    tabletPerStripe: 1,
    medicineScheduleId: "",
    domainId: "",
    regulatoryDomainId: "",

    // ---------------------------------------------------- media
    mainImage: null as File | null,
    mainImagePreview: DemoImage as string,
    mainImageId: "",
    subImages: [null, null, null] as (File | null)[],
    subImagePreviews: [DemoImage, DemoImage, DemoImage] as string[],
    subImageIds: ["", "", ""],

    // ---------------------------------------------------- composition
    salts: [] as SaltRow[],
    saltCompositionText: "",

    // ---------------------------------------------------- product_pkgn_dtls
    packagingText: "",
    packType: "",
    packUnit: "",
    packQuantity: "",
    baseUnit: "",
    multipackCount: "1",
    comboItems: "",
    offerFreeQty: "",

    // ---------------------------------------------------- medicine_attributes
    medProductFormId: "",
    releaseType: "",
    disintegrationType: "",
    route: "",

    // ---------------------------------------------------- product_attributes
    prodProductFormId: "",
    variant: "",
    flavour: "",
    targetGroup: "",
    ageMinMonths: "",
    ageMaxMonths: "",
    dietType: "",
    sugarStatus: "",
    attrKeyIngredients: "",

    // ---------------------------------------------------- GST (V2)
    hsnNumber: "",
    cgst: 0,
    sgst: 0,

    // ---------------------------------------------------- product_info (V2)
    introduction: "",
    howToUse: "",
    safetyAdvise: "",
    ifMiss: "",
    factBox: "",
    primaryUse: "",
    storage: "",
    commonSideEffect: "",
    alcoholInteraction: "",
    pregnancyInteraction: "",
    lactationInteraction: "",
    drivingInteraction: "",
    kidneyInteraction: "",
    liverInteraction: "",
    qAndA: "",
    howItWorks: "",
    interaction: "",
    keyIngredients: "",
    keyBenefits: "",
    source: "",
    productHighlights: "",

    // ---------------------------------------------------- classification
    categoryMappings: [] as CategoryMappingRow[],

    // ---------------------------------------------------- listing (V2)
    assignedListingCategories: [] as number[],
    initialListingCategories: [] as number[],
  });

  /**
   * Switching Is Medicine moves the product to the other category tree, so the
   * mappings and the attributes of the abandoned branch are cleared rather than
   * silently submitted against the wrong tree.
   */
  const handleChange = (field: string, value: any) => {
    setFormData((prev) => {
      if (field === "isMedicine" && value !== prev.isMedicine) {
        return {
          ...prev,
          isMedicine: value,
          categoryMappings: [],
          domainId: "",
          regulatoryDomainId: "",
          ...(value
            ? { prodProductFormId: "" }
            : {
                salts: [],
                saltCompositionText: "",
                medProductFormId: "",
                releaseType: "",
                disintegrationType: "",
                route: "",
                medicineScheduleId: "",
              }),
        };
      }
      return { ...prev, [field]: value };
    });

    // Typing is only worth tracking in edit mode, where each step saves on its
    // own. In create mode one button submits the lot.
    if (!isEdit) return;
    if (field !== "isMedicine") {
      markPending([field]);
      return;
    }
    // The flag empties the mappings and, when it goes off, the composition, so
    // those steps have to be saved for the change to stick.
    markPending([
      "isMedicine",
      ...CATEGORY_FIELDS,
      ...(value ? [] : SALT_FIELDS),
    ]);
  };

  /**
   * Writes the fields the reviewer accepted in the AI panel.
   *
   * The panel decides what may be written - it only offers fields that are
   * still blank, and anything already filled in has to be ticked deliberately -
   * so the patch is merged as given. The one thing handled here is a change of
   * Is Medicine, which moves the product to the other tree: the branch being
   * left behind is cleared first, exactly as handleChange does, and the patch
   * is applied over the top so the agent's answers for the new tree survive.
   */
  const applyEnrichment = (patch: Record<string, any>) => {
    const nextIsMedicine = patch.isMedicine ?? formData.isMedicine;

    setFormData((prev) => {
      const cleared: Record<string, any> =
        "isMedicine" in patch && patch.isMedicine !== prev.isMedicine
          ? {
              categoryMappings: [],
              domainId: "",
              regulatoryDomainId: "",
              ...(patch.isMedicine
                ? {
                    prodProductFormId: "",
                    variant: "",
                    flavour: "",
                    targetGroup: "",
                    ageMinMonths: "",
                    ageMaxMonths: "",
                    dietType: "",
                    sugarStatus: "",
                    attrKeyIngredients: "",
                  }
                : {
                    salts: [],
                    saltCompositionText: "",
                    medProductFormId: "",
                    releaseType: "",
                    disintegrationType: "",
                    route: "",
                    medicineScheduleId: "",
                  }),
            }
          : {};
      return { ...prev, ...cleared, ...patch };
    });

    // Every applied field is marked, so the steps holding them say so: in edit
    // mode they still need saving, and in create mode they still need reading.
    markPending([
      ...Object.keys(patch),
      ...("isMedicine" in patch && patch.isMedicine !== formData.isMedicine
        ? [...CATEGORY_FIELDS, ...(patch.isMedicine ? [] : SALT_FIELDS)]
        : []),
    ]);

    setIsEnrichOpen(false);

    // A medicine with no composition is the one gap the agent regularly leaves,
    // and the product cannot be created without it, so the form opens there.
    const nextSalts = patch.salts ?? formData.salts;
    if (nextIsMedicine && (!nextSalts || nextSalts.length === 0)) {
      toast(
        "Applied. This is a medicine with no composition yet - add the salt details.",
        { icon: "\u26a0\ufe0f" }
      );
      setActiveTab("salts");
      return;
    }
    toast.success("Applied. Review each tab before saving.");
  };

  const handleImageChange = (
    type: "main" | "sub",
    file: File | null,
    index?: number
  ) => {
    if (isEdit) markPending(["images"]);
    if (type === "main") {
      setFormData((prev) => ({
        ...prev,
        mainImage: file,
        mainImagePreview: file ? URL.createObjectURL(file) : DemoImage,
      }));
      return;
    }
    if (typeof index !== "number") return;
    setFormData((prev) => {
      const subImages = [...prev.subImages];
      const subImagePreviews = [...prev.subImagePreviews];
      subImages[index] = file;
      subImagePreviews[index] = file ? URL.createObjectURL(file) : DemoImage;
      return { ...prev, subImages, subImagePreviews };
    });
  };

  const handleRemoveImage = (type: "main" | "sub", index?: number) => {
    if (isEdit) markPending(["images"]);
    if (type === "main") {
      setFormData((prev) => ({
        ...prev,
        mainImage: null,
        mainImagePreview: DemoImage,
        mainImageId: "",
      }));
      return;
    }
    if (typeof index !== "number") return;
    setFormData((prev) => {
      const subImages = [...prev.subImages];
      const subImagePreviews = [...prev.subImagePreviews];
      const subImageIds = [...prev.subImageIds];
      subImages[index] = null;
      subImagePreviews[index] = DemoImage;
      subImageIds[index] = "";
      return { ...prev, subImages, subImagePreviews, subImageIds };
    });
  };

  const toggleListingCategory = (id: number) => {
    if (isEdit) markPending(["assignedListingCategories"]);
    setFormData((prev) => ({
      ...prev,
      assignedListingCategories: prev.assignedListingCategories.includes(id)
        ? prev.assignedListingCategories.filter((value) => value !== id)
        : [...prev.assignedListingCategories, id],
    }));
  };

  // -------------------------------------------------------------------------
  // Loading
  // -------------------------------------------------------------------------

  // getMarketerOptions unwraps the envelope itself and hands back a bare array.
  const loadMarketers = useCallback(async () => {
    const marketers = await InverntroyService.getMarketerOptions();
    setMarketerOptions(Array.isArray(marketers) ? marketers : []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadDropdowns = async () => {
      const [listing, marketers, schedules] = await Promise.all([
        InverntroyService.getCategoryList(),
        InverntroyService.getMarketerOptions(),
        ProductCatalogService.getScheduleOptions({ isActive: true }),
      ]);
      if (cancelled) return;
      setListingCategories(
        listing?.success && Array.isArray(listing.data) ? listing.data : []
      );
      setMarketerOptions(Array.isArray(marketers) ? marketers : []);
      setScheduleOptions(schedules?.success ? schedules.data || [] : []);
    };
    loadDropdowns();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!productId) return;
    let cancelled = false;

    /**
     * V3 details carries the catalogue; V2 details carries GST, long-form info,
     * media and listing categories, which V3 does not own. Both are read so the
     * form shows the whole product.
     */
    const loadProduct = async () => {
      setIsLoading(true);
      setPendingFields({});
      try {
        const [v3Res, v2Res] = await Promise.all([
          ProductCatalogService.getProductDetails(productId),
          InverntroyService.getProductDetails(productId),
        ]);
        if (cancelled) return;

        if (!v3Res?.success || !v3Res.data) {
          toast.error(v3Res?.message || "Failed to load product details");
          return;
        }

        const product = v3Res.data;
        const legacy = v2Res?.success ? v2Res.data || {} : {};
        const media: any[] = legacy.media || [];
        const mainMedia = media.find((item) => item.isMainImage);
        const subMedia = media.filter((item) => !item.isMainImage);

        setStoredIsMedicine(Boolean(product.isMedicine));
        setFormData((prev) => ({
          ...prev,
          name: product.name || "",
          description: product.description || "",
          brandName: product.brandName || "",
          marketerId: product.marketerId || "",
          countryOfOrigin: product.countryOfOrigin || "",
          categoryId: product.categoryId ?? prev.categoryId,
          isMedicine: Boolean(product.isMedicine),
          isRxRequired: product.isRxRequired ?? null,
          isPublished: product.isPublished ?? null,
          isActive: product.isActive ?? null,
          isReturnable: product.isReturnable ?? true,
          discretePackaging: product.discretePackaging ?? false,
          coldChainRequired: product.coldChainRequired ?? false,
          clinicOnly: product.clinicOnly ?? false,
          storageInstructions: product.storageInstructions || "",
          status: product.status || "",
          tabletPerStripe: product.tabletPerStripe ?? 1,
          medicineScheduleId:
            product.medicineScheduleId != null
              ? String(product.medicineScheduleId)
              : "",
          domainId: product.domainId || "",
          regulatoryDomainId: product.regulatoryDomainId || "",

          mainImagePreview: mainMedia?.productImagePath || DemoImage,
          mainImageId: mainMedia?.id ? String(mainMedia.id) : "",
          subImagePreviews: [0, 1, 2].map(
            (index) => subMedia[index]?.productImagePath || DemoImage
          ),
          subImageIds: [0, 1, 2].map((index) =>
            subMedia[index]?.id ? String(subMedia[index].id) : ""
          ),

          salts: (product.salts || []).map(
            (salt: any): SaltRow => ({
              saltId: salt.saltId,
              saltName: salt.saltName,
              strengthValue:
                salt.strengthValue != null ? String(salt.strengthValue) : "",
              strengthUnit: salt.strengthUnit || "",
              strengthBasisValue:
                salt.strengthBasisValue != null
                  ? String(salt.strengthBasisValue)
                  : "1",
              strengthBasisUnit: salt.strengthBasisUnit || "",
              concentrationBasis: salt.concentrationBasis || "",
            })
          ),
          saltCompositionText:
            product.medicineAttributes?.saltCompositionText || "",

          packagingText: product.packaging?.packagingText || "",
          packType: product.packaging?.packType || "",
          packUnit: product.packaging?.packUnit || "",
          packQuantity:
            product.packaging?.packQuantity != null
              ? String(product.packaging.packQuantity)
              : "",
          baseUnit: product.packaging?.baseUnit || "",
          multipackCount:
            product.packaging?.multipackCount != null
              ? String(product.packaging.multipackCount)
              : "1",
          comboItems: product.packaging?.comboItems || "",
          offerFreeQty:
            product.packaging?.offerFreeQty != null
              ? String(product.packaging.offerFreeQty)
              : "",

          medProductFormId: product.medicineAttributes?.productFormId || "",
          releaseType: product.medicineAttributes?.releaseType || "",
          disintegrationType:
            product.medicineAttributes?.disintegrationType || "",
          route: product.medicineAttributes?.route || "",

          prodProductFormId: product.productAttributes?.productFormId || "",
          variant: product.productAttributes?.variant || "",
          flavour: product.productAttributes?.flavour || "",
          targetGroup: product.productAttributes?.targetGroup || "",
          ageMinMonths:
            product.productAttributes?.ageMinMonths != null
              ? String(product.productAttributes.ageMinMonths)
              : "",
          ageMaxMonths:
            product.productAttributes?.ageMaxMonths != null
              ? String(product.productAttributes.ageMaxMonths)
              : "",
          dietType: product.productAttributes?.dietType || "",
          sugarStatus: product.productAttributes?.sugarStatus || "",
          attrKeyIngredients: product.productAttributes?.keyIngredients || "",

          categoryMappings: (product.categoryMappings || []).map(
            (mapping: any): CategoryMappingRow => ({
              hierarchyId: mapping.hierarchyId,
              primaryCategoryId: mapping.primaryCategoryId,
              primaryCategoryName: mapping.primaryCategoryName,
              subcategoryId: mapping.subcategoryId,
              subcategoryName: mapping.subcategoryName,
              groupId: mapping.groupId,
              groupName: mapping.groupName,
              isPrimary: Boolean(mapping.isPrimary),
            })
          ),

          hsnNumber: legacy.productDtls?.hsnNumber || "",
          cgst: legacy.productDtls?.newCgst ?? legacy.productDtls?.cgst ?? 0,
          sgst: legacy.productDtls?.newSgst ?? legacy.productDtls?.sgst ?? 0,

          introduction: legacy.productInfo?.introduction || "",
          howToUse: legacy.productInfo?.howToUse || "",
          safetyAdvise: legacy.productInfo?.safetyAdvise || "",
          ifMiss: legacy.productInfo?.ifMiss || "",
          factBox: legacy.productInfo?.factBox || "",
          primaryUse: legacy.productInfo?.primaryUse || "",
          storage: legacy.productInfo?.storage || "",
          commonSideEffect: legacy.productInfo?.commonSideEffect || "",
          alcoholInteraction: legacy.productInfo?.alcoholInteraction || "",
          pregnancyInteraction: legacy.productInfo?.pregnancyInteraction || "",
          lactationInteraction: legacy.productInfo?.lactationInteraction || "",
          drivingInteraction: legacy.productInfo?.drivingInteraction || "",
          kidneyInteraction: legacy.productInfo?.kidneyInteraction || "",
          liverInteraction: legacy.productInfo?.liverInteraction || "",
          qAndA: legacy.productInfo?.qAndA || "",
          howItWorks: legacy.productInfo?.howItWorks || "",
          interaction: legacy.productInfo?.interaction || "",
          keyIngredients: legacy.productInfo?.keyIngredients || "",
          keyBenefits: legacy.productInfo?.keyBenefits || "",
          source: legacy.productInfo?.source || "",
          productHighlights: legacy.productInfo?.productHighlights || "",

          assignedListingCategories: (
            legacy.productListingCategories || []
          ).map((item: any) => (typeof item === "object" ? item.id : item)),
          initialListingCategories: (
            legacy.productListingCategories || []
          ).map((item: any) => (typeof item === "object" ? item.id : item)),
        }));
      } catch (error) {
        if (!cancelled) toast.error("Failed to load product details");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadProduct();
    return () => {
      cancelled = true;
    };
  }, [productId]);

  // -------------------------------------------------------------------------
  // Payload builders
  // -------------------------------------------------------------------------

  /** Numbers arrive from inputs as strings; blank must stay unset, not 0. */
  const optionalNumber = (value: string | number) =>
    value === "" || value === null || value === undefined
      ? null
      : Number(value);

  const optionalText = (value: string) => (value.trim() === "" ? null : value);

  const buildProductMstr = () => ({
    name: formData.name.trim(),
    description: optionalText(formData.description),
    categoryId: formData.categoryId,
    brandName: optionalText(formData.brandName),
    marketerId: formData.marketerId || null,
    countryOfOrigin: optionalText(formData.countryOfOrigin),
    isMedicine: formData.isMedicine,
    isRxRequired: Boolean(formData.isRxRequired),
    isPublished: Boolean(formData.isPublished),
    isActive: Boolean(formData.isActive),
    isReturnable: formData.isReturnable,
    discretePackaging: formData.discretePackaging,
    coldChainRequired: formData.coldChainRequired,
    clinicOnly: formData.clinicOnly,
    storageInstructions: optionalText(formData.storageInstructions),
    ...(formData.status ? { status: formData.status } : {}),
    tabletPerStripe: optionalNumber(formData.tabletPerStripe),
    domainId: formData.domainId || null,
    regulatoryDomainId: formData.regulatoryDomainId || null,
    medicineScheduleId: formData.isMedicine
      ? optionalNumber(formData.medicineScheduleId)
      : null,
  });

  const buildProductPkgn = () => ({
    packagingText: optionalText(formData.packagingText),
    packType: optionalText(formData.packType),
    packUnit: formData.packUnit || null,
    packQuantity: optionalNumber(formData.packQuantity),
    baseUnit: formData.baseUnit || null,
    multipackCount: Number(formData.multipackCount || 1),
    comboItems: optionalText(formData.comboItems),
    offerFreeQty: optionalNumber(formData.offerFreeQty),
  });

  const buildMedicineAttributes = () => ({
    productFormId: formData.medProductFormId || null,
    releaseType: formData.releaseType || null,
    disintegrationType: formData.disintegrationType || null,
    route: formData.route || null,
    saltCompositionText: optionalText(formData.saltCompositionText),
  });

  const buildProductAttributes = () => ({
    productFormId: formData.prodProductFormId || null,
    variant: optionalText(formData.variant),
    flavour: optionalText(formData.flavour),
    targetGroup: optionalText(formData.targetGroup),
    ageMinMonths: optionalNumber(formData.ageMinMonths),
    ageMaxMonths: optionalNumber(formData.ageMaxMonths),
    dietType: formData.dietType || null,
    sugarStatus: formData.sugarStatus || null,
    keyIngredients: optionalText(formData.attrKeyIngredients),
  });

  const buildSalts = () =>
    formData.salts
      .filter((salt) => salt.saltId)
      .map((salt) => ({
        saltId: salt.saltId,
        strengthValue: optionalNumber(salt.strengthValue),
        strengthUnit: salt.strengthUnit || null,
        // The basis is a denominator, so it must stay positive; blank or a
        // non-positive entry means "per one unit".
        strengthBasisValue:
          Number(salt.strengthBasisValue) > 0
            ? Number(salt.strengthBasisValue)
            : 1,
        strengthBasisUnit: salt.strengthBasisUnit || null,
        concentrationBasis: salt.concentrationBasis || null,
      }));

  const buildMappings = () =>
    formData.categoryMappings.map((mapping) => ({
      hierarchyId: mapping.hierarchyId,
      isPrimary: mapping.isPrimary,
    }));

  const buildProductInfo = () => ({
    introduction: formData.introduction,
    howToUse: formData.howToUse,
    safetyAdvise: formData.safetyAdvise,
    ifMiss: formData.ifMiss,
    factBox: formData.factBox,
    primaryUse: formData.primaryUse,
    storage: formData.storage,
    commonSideEffect: formData.commonSideEffect,
    alcoholInteraction: formData.alcoholInteraction,
    pregnancyInteraction: formData.pregnancyInteraction,
    lactationInteraction: formData.lactationInteraction,
    drivingInteraction: formData.drivingInteraction,
    kidneyInteraction: formData.kidneyInteraction,
    liverInteraction: formData.liverInteraction,
    qAndA: formData.qAndA,
    howItWorks: formData.howItWorks,
    interaction: formData.interaction,
    keyIngredients: formData.keyIngredients,
    keyBenefits: formData.keyBenefits,
    source: formData.source,
    productHighlights: formData.productHighlights,
  });

  const hasProductInfo = () =>
    Object.values(buildProductInfo()).some((value) => `${value}`.trim() !== "");

  /** Images go through the V2 basic-details endpoint, which owns media_mstr. */
  const buildImagePayload = () => {
    const payload = new FormData();
    let hasAny = false;
    if (formData.mainImage) {
      payload.append("main_image", formData.mainImage);
      if (formData.mainImageId)
        payload.append("mainImageId", formData.mainImageId);
      hasAny = true;
    }
    for (let index = 0; index < 3; index += 1) {
      const file = formData.subImages[index];
      if (file) {
        payload.append(`sub_image_${index + 1}`, file);
        if (formData.subImageIds[index]) {
          payload.append(
            `subImage${index + 1}Id`,
            formData.subImageIds[index]
          );
        }
        hasAny = true;
      }
    }
    return hasAny ? payload : null;
  };

  // -------------------------------------------------------------------------
  // Create
  // -------------------------------------------------------------------------

  const validateForCreate = () => {
    if (!formData.name.trim()) return "Product name is required";
    if (
      formData.isRxRequired === null ||
      formData.isPublished === null ||
      formData.isActive === null
    ) {
      return "Select Rx Required, Published and Active in Basic Details";
    }
    if (formData.isMedicine && buildSalts().length === 0) {
      return "A medicine needs at least one salt in Salt Details";
    }
    return null;
  };

  const handleCreateProduct = async () => {
    const error = validateForCreate();
    if (error) {
      toast.error(error);
      return;
    }

    setIsUpdating(true);
    try {
      const createRes = await ProductCatalogService.createProduct({
        productMstr: buildProductMstr(),
        productPkgn: buildProductPkgn(),
        ...(formData.isMedicine
          ? { medicineAttributes: buildMedicineAttributes(), salts: buildSalts() }
          : { productAttributes: buildProductAttributes() }),
        categoryMappings: buildMappings(),
        updationRemarks: "Created from the admin products screen",
      });

      if (!createRes?.success) {
        toast.error(createRes?.message || "Failed to create product");
        return;
      }

      const newProductId: string | undefined =
        createRes.data?.id || createRes.data?.productId;

      // The catalogue row exists now. The four sections V3 does not own are
      // written after it, and each reports its own failure so a partial result
      // is never announced as a success.
      if (newProductId) {
        const followUps: string[] = [];

        const imagePayload = buildImagePayload();
        if (imagePayload) {
          const res = await InverntroyService.updateProductBasicV2(
            newProductId,
            imagePayload
          );
          if (!res?.success) followUps.push("images");
        }

        if (formData.hsnNumber || formData.cgst || formData.sgst) {
          const res = await InverntroyService.updateProductTaxesV2(
            newProductId,
            {
              productDtls: {
                hsnNumber: formData.hsnNumber,
                cgst: formData.cgst,
                sgst: formData.sgst,
              },
            }
          );
          if (!res?.success) followUps.push("GST details");
        }

        if (hasProductInfo()) {
          const res = await InverntroyService.updateProductInfoV2(
            newProductId,
            { productInfo: buildProductInfo() }
          );
          if (!res?.success) followUps.push("product info");
        }

        if (formData.assignedListingCategories.length > 0) {
          const res =
            await InverntroyService.updateProductListingCategoriesV2(
              newProductId,
              { addIds: formData.assignedListingCategories }
            );
          if (!res?.success) followUps.push("listing categories");
        }

        if (followUps.length > 0) {
          toast.error(
            `Product created, but ${followUps.join(
              ", "
            )} could not be saved. Open the product and retry those sections.`
          );
          onSuccess();
          onClose();
          return;
        }
      }

      toast.success("Product created successfully");
      onSuccess();
      onClose();
    } catch (err) {
      toast.error("An error occurred while creating the product");
    } finally {
      setIsUpdating(false);
    }
  };

  // -------------------------------------------------------------------------
  // Per-section updates (edit mode)
  // -------------------------------------------------------------------------

  /**
   * `persistedFields` is what the call actually writes, which is not always the
   * fields of the step it belongs to, so the step markers stay honest.
   */
  const runUpdate = async (
    label: string,
    action: () => Promise<{ success?: boolean; message?: string }>,
    persistedFields: string[]
  ) => {
    setIsUpdating(true);
    try {
      const res = await action();
      if (res?.success) {
        clearPending(persistedFields);
        toast.success(res.message || `${label} updated`);
        onSuccess();
      } else {
        toast.error(res?.message || `Failed to update ${label.toLowerCase()}`);
      }
    } catch (error) {
      toast.error(`Failed to update ${label.toLowerCase()}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateBasicDetails = () =>
    runUpdate(
      "Basic details",
      async () => {
        if (
          formData.isRxRequired === null ||
          formData.isPublished === null ||
          formData.isActive === null
        ) {
          return {
            success: false,
            message: "Select Rx Required, Published and Active",
          };
        }
        const res = await ProductCatalogService.updateProduct(productId, {
          productMstr: buildProductMstr(),
        });
        if (!res?.success) return res;

        // Turning off Is Medicine strands the salt rows, so drop them here.
        if (storedIsMedicine && !formData.isMedicine) {
          const clearRes = await ProductCatalogService.replaceComposition(
            productId,
            [],
            "Composition cleared because the product is no longer a medicine"
          );
          if (!clearRes?.success) {
            return {
              success: false,
              message:
                "Basic details saved, but the old salt composition could not be cleared",
            };
          }
          setStoredIsMedicine(false);
        } else if (!storedIsMedicine && formData.isMedicine) {
          setStoredIsMedicine(true);
        }

        const imagePayload = buildImagePayload();
        if (imagePayload) {
          const imageRes = await InverntroyService.updateProductBasicV2(
            productId,
            imagePayload
          );
          if (!imageRes?.success) {
            return {
              success: false,
              message:
                "Basic details saved, but the images could not be uploaded",
            };
          }
        }
        return res;
      },
      [
        ...BASIC_FIELDS,
        // The domain pair sits on product_mstr, so it is written from here too.
        "domainId",
        "regulatoryDomainId",
        // And the composition is wiped when the flag goes off.
        ...(storedIsMedicine && !formData.isMedicine ? SALT_FIELDS : []),
      ]
    );

  const handleUpdateComposition = () =>
    runUpdate(
      "Composition",
      async () => {
        const res = await ProductCatalogService.replaceComposition(
          productId,
          buildSalts()
        );
        if (!res?.success) return res;
        // saltCompositionText lives on medicine_attributes, not the salt rows.
        return ProductCatalogService.updateProduct(productId, {
          medicineAttributes: buildMedicineAttributes(),
        });
      },
      // The medicine attributes go up with it, so that step is saved as well.
      [...SALT_FIELDS, ...MEDICINE_ATTR_FIELDS]
    );

  const handleUpdatePackaging = () =>
    runUpdate(
      "Packaging",
      () =>
        ProductCatalogService.updateProduct(productId, {
          productPkgn: buildProductPkgn(),
        }),
      PACKAGING_FIELDS
    );

  const handleUpdateAttributes = () =>
    runUpdate(
      "Attributes",
      () =>
        ProductCatalogService.updateProduct(
          productId,
          formData.isMedicine
            ? { medicineAttributes: buildMedicineAttributes() }
            : { productAttributes: buildProductAttributes() }
        ),
      formData.isMedicine
        ? // medicineAttributes carries saltCompositionText, which is edited on
          // the composition step.
          [...MEDICINE_ATTR_FIELDS, "saltCompositionText"]
        : PRODUCT_ATTR_FIELDS
    );

  const handleUpdateCategoryMappings = () =>
    runUpdate(
      "Category mapping",
      async () => {
        const res = await ProductCatalogService.replaceCategoryMappings(
          productId,
          buildMappings()
        );
        if (!res?.success) return res;
        // The resolved domain is stored on product_mstr, so it saves with it.
        return ProductCatalogService.updateProduct(productId, {
          productMstr: {
            domainId: formData.domainId || null,
            regulatoryDomainId: formData.regulatoryDomainId || null,
          },
        });
      },
      CATEGORY_FIELDS
    );

  const handleUpdateGst = () =>
    runUpdate(
      "Taxes",
      () =>
        InverntroyService.updateProductTaxesV2(productId, {
          productDtls: {
            hsnNumber: formData.hsnNumber,
            cgst: formData.cgst,
            sgst: formData.sgst,
          },
        }),
      GST_FIELDS
    );

  const handleUpdateInfo = () =>
    runUpdate(
      "Product info",
      () =>
        InverntroyService.updateProductInfoV2(productId, {
          productInfo: buildProductInfo(),
        }),
      INFO_FIELDS
    );

  const handleUpdateListingCategories = () =>
    runUpdate(
      "Listing categories",
      async () => {
        const addIds = formData.assignedListingCategories.filter(
          (id) => !formData.initialListingCategories.includes(id)
        );
        const removeIds = formData.initialListingCategories.filter(
          (id) => !formData.assignedListingCategories.includes(id)
        );
        if (addIds.length === 0 && removeIds.length === 0) {
          return { success: true, message: "No changes to listing categories" };
        }
        const res = await InverntroyService.updateProductListingCategoriesV2(
          productId,
          {
            ...(addIds.length ? { addIds } : {}),
            ...(removeIds.length ? { removeIds } : {}),
          }
        );
        if (res?.success) {
          setFormData((prev) => ({
            ...prev,
            initialListingCategories: prev.assignedListingCategories,
          }));
        }
        return res;
      },
      LISTING_FIELDS
    );

  // -------------------------------------------------------------------------
  // Steps
  // -------------------------------------------------------------------------

  /** The salt step only exists for medicines, so the step list is derived. */
  const tabs = useMemo(() => {
    const all: { id: TabId; label: string }[] = [
      { id: "basic", label: "Basic Details" },
      ...(formData.isMedicine
        ? ([{ id: "salts", label: "Salt Details" }] as const)
        : []),
      { id: "packaging", label: "Packaging Details" },
      { id: "gst", label: "GST Details" },
      {
        id: "attributes",
        label: formData.isMedicine
          ? "Medicine Attributes"
          : "Product Attributes",
      },
      { id: "category", label: "Category & Domain" },
      { id: "info", label: "Product Info" },
      { id: "listing", label: "Listing Categories" },
    ];
    return all;
  }, [formData.isMedicine]);

  // Keep the selection valid when the salt step appears or disappears.
  useEffect(() => {
    if (!tabs.some((tab) => tab.id === activeTab)) setActiveTab("basic");
  }, [tabs, activeTab]);

  const activeIndex = Math.max(
    0,
    tabs.findIndex((tab) => tab.id === activeTab)
  );

  /** How many unsaved fields sit on each step, for the markers on the strip. */
  const pendingByTab = useMemo(() => {
    const counts: Partial<Record<TabId, number>> = {};
    Object.keys(pendingFields).forEach((field) => {
      const tab = FIELD_TABS[field];
      if (!tab) return;
      counts[tab] = (counts[tab] || 0) + 1;
    });
    return counts;
  }, [pendingFields]);

  const pendingTabs = tabs.filter((tab) => (pendingByTab[tab.id] || 0) > 0);

  /**
   * Saving one step at a time makes it easy to close the form with work still
   * sitting in another step, so leaving with unsaved fields asks first.
   */
  const handleClose = () => {
    if (isEdit && pendingTabs.length > 0) {
      const names = pendingTabs.map((tab) => tab.label).join(", ");
      const confirmed = window.confirm(
        `These steps have changes that have not been saved yet: ${names}.\n\n` +
          "Each step saves on its own, so closing now loses them. Close anyway?"
      );
      if (!confirmed) return;
    }
    onClose();
  };

  return (
    <section className="w-full pb-2">
      <div className="flex items-center justify-between mb-3">
        <h1
          onClick={handleClose}
          className="flex items-center justify-start gap-2 cursor-pointer"
        >
          {pathname !== "/admin/stocks-orders/items" && (
            <CircleChevronLeft className="w-6 h-6 bg-[#808080] text-white rounded-full" />
          )}
          <span className="font-medium text-lg text-emerald-600">
            {isEdit ? "Update Product" : "Add New Product"}
          </span>
          {isEdit && formData.name && (
            <span className="font-semibold text-lg text-gray-800">
              - {formData.name}
            </span>
          )}
        </h1>

        <button
          type="button"
          onClick={() => setIsEnrichOpen(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-emerald-700 border border-emerald-200 bg-emerald-50 rounded-lg hover:bg-emerald-100"
        >
          <Sparkles className="w-4 h-4" />
          Get Details From AI
        </button>
      </div>

      {isEnrichOpen && (
        <ProductEnrichPanel
          formData={formData}
          isEdit={isEdit}
          marketerOptions={marketerOptions}
          onApply={applyEnrichment}
          onMarketersChanged={loadMarketers}
          onClose={() => setIsEnrichOpen(false)}
        />
      )}

      <div className="border border-[#DCDCDC] w-full bg-white rounded-xl mt-3 p-4">
        {isLoading ? (
          <ProductFormSkeleton />
        ) : (
          <div className="flex flex-col gap-6">
            {/* Which steps still need attention, named so none is missed. */}
            {pendingTabs.length > 0 && (
              <div className="flex items-start gap-2 border border-amber-200 bg-amber-50 rounded-xl px-4 py-3">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-800">
                  {isEdit ? (
                    <>
                      <span className="font-medium">
                        Not saved yet, and each step saves on its own:
                      </span>{" "}
                      press Update on{" "}
                    </>
                  ) : (
                    <>
                      <span className="font-medium">Filled in by AI:</span>{" "}
                      read through{" "}
                    </>
                  )}
                  {pendingTabs.map((tab, index) => (
                    <span key={tab.id}>
                      {index > 0 && ", "}
                      <button
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        className="font-medium underline hover:text-amber-900"
                      >
                        {tab.label} ({pendingByTab[tab.id]})
                      </button>
                    </span>
                  ))}
                  .
                </div>
              </div>
            )}

            <div className="w-full flex overflow-x-auto border-b border-gray-200">
              {tabs.map((tab, index) => {
                const pendingCount = pendingByTab[tab.id] || 0;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? "border-emerald-500 text-emerald-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    <span>
                      {!isEdit && (
                        <span className="text-gray-400 mr-1.5">
                          {index + 1}.
                        </span>
                      )}
                      {tab.label}
                    </span>
                    {pendingCount > 0 && (
                      <span
                        title={
                          isEdit
                            ? `${pendingCount} field(s) changed here and not saved yet`
                            : `${pendingCount} field(s) filled in by AI, not read yet`
                        }
                        className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold"
                      >
                        {pendingCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="flex-1 bg-white min-h-[50vh]">
              {activeTab === "basic" && (
                <BasicDetailsTab
                  data={formData}
                  onChange={handleChange}
                  onImageChange={handleImageChange}
                  onRemoveImage={handleRemoveImage}
                  isEdit={isEdit}
                  isLoading={isUpdating}
                  onUpdate={handleUpdateBasicDetails}
                  marketerOptions={marketerOptions}
                  scheduleOptions={scheduleOptions}
                />
              )}

              {activeTab === "salts" && (
                <ProductSaltDtlsTab
                  salts={formData.salts}
                  saltCompositionText={formData.saltCompositionText}
                  onChange={handleChange}
                  isEdit={isEdit}
                  isLoading={isUpdating}
                  onUpdate={handleUpdateComposition}
                />
              )}

              {activeTab === "packaging" && (
                <ProductPackagingDtlsTab
                  data={formData}
                  onChange={handleChange}
                  isEdit={isEdit}
                  isLoading={isUpdating}
                  onUpdate={handleUpdatePackaging}
                />
              )}

              {activeTab === "gst" && (
                <ProductGstTab
                  data={formData}
                  onChange={handleChange}
                  isEdit={isEdit}
                  isLoading={isUpdating}
                  onUpdate={handleUpdateGst}
                />
              )}

              {activeTab === "attributes" && (
                <ProductAttributesTab
                  data={formData}
                  onChange={handleChange}
                  isEdit={isEdit}
                  isLoading={isUpdating}
                  onUpdate={handleUpdateAttributes}
                />
              )}

              {activeTab === "category" && (
                <ProductCategoryMappingTab
                  data={formData}
                  onChange={handleChange}
                  isEdit={isEdit}
                  isLoading={isUpdating}
                  onUpdate={handleUpdateCategoryMappings}
                />
              )}

              {activeTab === "info" && (
                <ProductInfoTab
                  data={formData}
                  onChange={handleChange}
                  isEdit={isEdit}
                  isLoading={isUpdating}
                  onUpdate={handleUpdateInfo}
                />
              )}

              {activeTab === "listing" && (
                <ProductCategoryTab
                  assignedListingCategories={formData.assignedListingCategories}
                  listingCategories={listingCategories}
                  toggleListingCategory={toggleListingCategory}
                  isEdit={isEdit}
                  isLoading={isUpdating}
                  onUpdate={handleUpdateListingCategories}
                />
              )}

              {!isEdit && (
                <div className="flex justify-between items-center mt-8 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() =>
                      setActiveTab(tabs[Math.max(0, activeIndex - 1)].id)
                    }
                    disabled={activeIndex === 0 || isUpdating}
                    className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  {activeIndex < tabs.length - 1 ? (
                    <button
                      type="button"
                      onClick={() => setActiveTab(tabs[activeIndex + 1].id)}
                      className="px-4 py-2 text-white bg-emerald-600 rounded-lg hover:bg-emerald-700"
                    >
                      Next
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleCreateProduct}
                      disabled={isUpdating}
                      className="px-6 py-2 text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50"
                    >
                      {isUpdating ? "Creating..." : "Create Product"}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
